import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const FADE = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

// Same canonical relation order used in the demo end-of-video form, with
// the two newborn editions promoted to the top.
const RELATION_KEYS = [
    "baby_boy", "baby_girl",
    "parent", "spouse", "grandparent", "sibling",
    "friend", "self", "aged_care", "other",
];

export function Waitlist() {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [relation, setRelation] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/waitlist", { email, name, relation });
            setDone(true);
            toast.success(t("waitlist.toast_success"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("waitlist.toast_error"));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <section className="relative py-32 px-6 bg-white" data-testid="waitlist-section" id="waitlist">
            <motion.div {...FADE} className="max-w-2xl mx-auto text-center">
                <div className="flex justify-center mb-6">
                    <ForgetMeNotIcon size={40} />
                </div>
                <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                    {t("waitlist.eyebrow")}
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2D2B2A] leading-tight">
                    {t("waitlist.headline_part1")} <em className="italic text-[#4A5A95]">{t("waitlist.headline_emphasis")}</em>
                </h2>
                <p className="mt-6 text-[#5C5A59] leading-relaxed font-light">
                    {t("waitlist.body")}
                </p>

                {done ? (
                    <div className="mt-10 rounded-2xl bg-[#F4F6FB] p-8 text-[#4A5A95] font-medium" data-testid="waitlist-success">
                        {t("waitlist.success")}
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-10 space-y-3 max-w-md mx-auto text-left" data-testid="waitlist-form">
                        <input
                            type="text"
                            placeholder={t("waitlist.name_placeholder")}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-5 py-3.5 rounded-full text-sm bg-[#F4F6FB] text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB] outline-none transition-all"
                            data-testid="waitlist-name"
                        />
                        <select
                            value={relation}
                            onChange={(e) => setRelation(e.target.value)}
                            className="w-full px-5 py-3.5 rounded-full text-sm bg-[#F4F6FB] text-[#2D2B2A] border border-[#E5E1D8] focus:border-[#8C9ECB] outline-none transition-all appearance-none cursor-pointer"
                            data-testid="waitlist-relation"
                        >
                            <option value="">{t("waitlist.relation_label")}</option>
                            {RELATION_KEYS.map((k) => (
                                <option key={k} value={k}>{t(`waitlist.relations.${k}`)}</option>
                            ))}
                        </select>
                        <input
                            type="email"
                            required
                            placeholder={t("waitlist.email_placeholder")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-5 py-3.5 rounded-full text-sm bg-[#F4F6FB] text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB] outline-none transition-all"
                            data-testid="waitlist-email"
                        />
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-sm transition-all hover:-translate-y-0.5 disabled:opacity-60"
                            data-testid="waitlist-submit"
                        >
                            {submitting ? t("waitlist.submitting") : t("waitlist.submit")}
                            <ArrowRight weight="bold" size={16} />
                        </button>
                    </form>
                )}
            </motion.div>
        </section>
    );
}
